import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Button, Typography, Tabs, Form, Space, Divider, Alert } from 'antd';
import { LockOutlined, MailOutlined, UserOutlined, PhoneOutlined, SafetyOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { shopConfig } from '../../config/shop.config';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState<string>('');
  const [registerError, setRegisterError] = useState<string>('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();
  const { login, register, isAuthenticated, user } = useAuth();

  // Helper function to get role-based dashboard route
  const getRoleDashboard = (role: string): string => {
    const roleRoutes: Record<string, string> = {
      owner: '/owner/dashboard',
      advisor: '/advisor/dashboard',
      technician: '/technician/dashboard',
      customer: '/portal/dashboard',
    };
    return roleRoutes[role] || '/admin/dashboard';
  };

  const sessionEnded = typeof window !== 'undefined' && sessionStorage.getItem('cnf_session_ended') === '1';
  useEffect(() => { if (sessionEnded) sessionStorage.removeItem('cnf_session_ended'); }, [sessionEnded]);

  useEffect(() => {
    // Check if session expired
    const expired = sessionStorage.getItem('session_expired');
    if (expired === 'true') {
      setSessionExpired(true);
      sessionStorage.removeItem('session_expired');
    }
  }, []);

  useEffect(() => {
    // If already authenticated, redirect based on role
    if (isAuthenticated && user) {
      navigate(getRoleDashboard(user.role));
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogin = async (values: any) => {
    setLoading(true);
    setLoginError(''); // Clear previous errors
    try {
      await login({
        email: values.email,
        password: values.password,
      });
      // The user will be set in the context, so we need to wait for it
      // The useEffect above will handle the redirect based on the user's role
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Login failed. Please check your credentials.';
      setLoginError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values: any) => {
    setLoading(true);
    setRegisterError(''); // Clear previous errors
    try {
      await register({
        email: values.email,
        password: values.password,
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone,
        role: 'customer', // Force customer role for self-registration
      });
      navigate('/portal/dashboard', { replace: true });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Registration failed. Please try again.';
      setRegisterError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: '450px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Space
          direction="vertical"
          size="large"
          style={{ width: '100%', textAlign: 'center' }}
        >
          {/* Logo/Icon */}
          <div
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#2563eb',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
            }}
          >
            <SafetyOutlined style={{ fontSize: '40px', color: 'white' }} />
          </div>

          {/* Title */}
          <div>
            <Title level={3} style={{ marginBottom: '8px' }}>
              {shopConfig.name}
            </Title>
            <Text type="secondary">Auto Shop Management System</Text>
          </div>

          {/* Calm inactivity sign-out banner */}
          {sessionEnded && (
            <Alert
              type="info"
              showIcon
              message="Signed out after inactivity. Your work was saved."
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Session Expired Alert */}
          {sessionExpired && (
            <Alert
              message="Session Expired"
              description="Your session has expired due to inactivity. Please log in again to continue."
              type="warning"
              showIcon
              closable
              onClose={() => setSessionExpired(false)}
            />
          )}

          {/* Tabs for Login/Register */}
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key as 'login' | 'register');
              // Clear errors when switching tabs for better UX
              setLoginError('');
              setRegisterError('');
            }}
            centered
            items={[
              {
                key: 'login',
                label: 'Sign In',
                children: (
                  <Form
                    name="login"
                    onFinish={handleLogin}
                    layout="vertical"
                    requiredMark={false}
                    onFieldsChange={() => setLoginError('')} // Clear error when user types
                  >
                    {loginError && (
                      <Alert
                        message="Login Failed"
                        description={loginError}
                        type="error"
                        showIcon
                        closable
                        onClose={() => setLoginError('')}
                        style={{ marginBottom: 16 }}
                      />
                    )}
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' },
                      ]}
                    >
                      <Input
                        size="large"
                        prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
                        placeholder="Email"
                        autoComplete="email"
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      label="Password"
                      rules={[{ required: true, message: 'Please enter your password' }]}
                    >
                      <Input.Password
                        size="large"
                        prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                        placeholder="Password"
                        autoComplete="current-password"
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        loading={loading}
                        block
                        icon={<LockOutlined />}
                      >
                        Sign In
                      </Button>
                    </Form.Item>

                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Staff accounts are created by administrators
                    </Text>
                  </Form>
                ),
              },
              {
                key: 'register',
                label: 'Register',
                children: (
                  <Form
                    name="register"
                    onFinish={handleRegister}
                    layout="vertical"
                    requiredMark={false}
                    onFieldsChange={() => setRegisterError('')} // Clear error when user types
                  >
                    {registerError && (
                      <Alert
                        message="Registration Failed"
                        description={registerError}
                        type="error"
                        showIcon
                        closable
                        onClose={() => setRegisterError('')}
                        style={{ marginBottom: 16 }}
                      />
                    )}
                    <Form.Item
                      name="first_name"
                      label="First Name"
                      rules={[{ required: true, message: 'Please enter your first name' }]}
                    >
                      <Input
                        size="large"
                        prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                        placeholder="First Name"
                        autoComplete="given-name"
                      />
                    </Form.Item>

                    <Form.Item
                      name="last_name"
                      label="Last Name"
                      rules={[{ required: true, message: 'Please enter your last name' }]}
                    >
                      <Input
                        size="large"
                        prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                        placeholder="Last Name"
                        autoComplete="family-name"
                      />
                    </Form.Item>

                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' },
                      ]}
                    >
                      <Input
                        size="large"
                        prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
                        placeholder="Email"
                        autoComplete="email"
                      />
                    </Form.Item>

                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[{ required: true, message: 'Please enter your phone number' }]}
                    >
                      <Input
                        size="large"
                        prefix={<PhoneOutlined style={{ color: '#9ca3af' }} />}
                        placeholder="Phone Number"
                        autoComplete="tel"
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      label="Password"
                      rules={[
                        { required: true, message: 'Please enter a password' },
                        // Length only, matching the server (utils/password.ts and
                        // passwordSchema). Character-class rules were dropped
                        // 2026-07-30; leaving the pattern here would refuse
                        // passwords the server accepts.
                        { min: 8, message: 'Password must be at least 8 characters' },
                        { max: 72, message: 'Password must not exceed 72 characters' },
                      ]}
                      extra={
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          At least 8 characters.
                        </Text>
                      }
                    >
                      <Input.Password
                        size="large"
                        prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                        placeholder="Password"
                        autoComplete="new-password"
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirm_password"
                      label="Confirm Password"
                      dependencies={['password']}
                      rules={[
                        { required: true, message: 'Please confirm your password' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        size="large"
                        prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                        placeholder="Confirm Password"
                        autoComplete="new-password"
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        loading={loading}
                        block
                      >
                        Create Customer Account
                      </Button>
                    </Form.Item>

                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Register as a customer to view your service history and estimates
                    </Text>
                  </Form>
                ),
              },
            ]}
          />

          <Divider />

          {/* Back to Home Link */}
          <Button
            type="link"
            onClick={() => navigate('/')}
            style={{ color: '#6b7280' }}
          >
            ← Back to Home
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default Login;
